import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePositionSizing } from '../../hooks/usePositionSizing';
import {
    Box,
    Button,
    Chip,
    Snackbar,
    Alert,
    Typography,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid2,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    Add as AddIcon,
    FileDownloadOutlined as FileDownloadOutlinedIcon,
} from '@mui/icons-material';
import { Trash2, Undo2 } from 'lucide-react';
import PropTypes from 'prop-types';
import ToolbarInputs from './ToolbarInputs';
import { formatInputValue, formatValue } from './positionSizingFormat';
import EditableCellWithUndo from './EditableCellWithUndo';
import { outputHeatmapClassName } from './outputHeatmapClasses';
import {
    compactIconButtonSx,
    compactPanelPaddingSx,
} from './tableStyles';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import BusyOverlay from '../commonComponents/BusyOverlay';
import * as XLSX from 'xlsx';
import { cardClasses, negativeCell } from '../../styles/tailwindClasses';
import { getUploadedIdentifierForRow } from '../../utils/identifierDisplay';
import { securityIdentityPresentation } from '../../utils/securityIdentityPresentation';
import { analyticsPanelSx } from '../../styles/analyticsMsiStyles';
import AddWatchlistSecuritiesDialog from './AddWatchlistSecuritiesDialog';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from '../commonComponents/SecurityIdentityFieldSelector';
import { isWatchlistGroupingAttribute } from '../../utils/watchlistAttributePresentation';
import { buildPositionSizingOutputHeatmap } from '../../utils/positionSizingOutputHeatmap';
import {
    WORKSPACE_DIMENSIONS,
    workspaceActionClusterSx,
} from '../../styles/workspaceChromeStyles';
import DashboardToolbarPortal from '../dashboard/components/DashboardToolbarPortal';
import DataTable from '../data_table/DataTable';
import { sortByAbsoluteNumber, sortByNumber, sortByText } from '../data_table/sorting';
import KitIconButton from '../ui/IconButton';
import KitTooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';

const PINNED_COLUMNS = Object.freeze(['row_actions', 'identity_summary']);
const NO_SORTING = Object.freeze([]);
const EMPTY_POS_SIZING_ROW_COUNT = 100;
const CELL_WIDTH = 120;
// Row states, as the row's background so the pinned cells follow it.
const ROW_STATE_CLASS_NAMES = Object.freeze({
    placeholder: 'pointer-events-none',
    deleted: '!bg-ui-danger-bg text-ui-danger',
    // Deleted text stays red on the scatter-linked row's selected tint.
    deletedLinked: 'text-ui-danger',
    'incomplete-draft': '!bg-ui-warning-bg',
    'complete-draft': '!bg-ui-success-bg',
});
// Input cells: the input tint, the edited-but-unsaved tint, and inputs the
// row's other input has locked (a new row keeps its draft tint).
const INPUT_CELL_CLASS_NAMES = Object.freeze({
    input: 'bg-ui-info-bg',
    dirty: 'bg-ui-warning-bg',
    locked: 'pointer-events-none bg-ui-disabled-fill text-ui-disabled-text',
    lockedDraft: 'pointer-events-none text-ui-disabled-text',
});
const normalizeCogniveId = (value) => {
    if (value == null) return null;
    const normalized = String(value).trim();
    return normalized || null;
};
const isNegative = (value) => value != null && value !== '' && Number(value) < 0;

export default function PositionSizingTable({
    posSizingData,
    industries = [],
    setPosSizingData,
    displayUnit,
    serverMaxPosSize,
    setServerMaxPosSize,
    serverNormalVol,
    setServerNormalVol,
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    onPosSizingSaved,
    onEditingStateChange,
    onSavingStateChange,
    onPendingChangesStateChange,
    linkedRowId = null,
    linkedRowRequestId = 0,
    onVisualizationRowsChange,
    importsControl = null,
    contextUpdatesControl = null,
    toolbarActive = true,
    wrapInGridItem = true,
    gridSize = { md: 12 },
    loading = false,
    readOnly = false,
    posSizingPublicationBaseRef,
    watchlistAttributeSnapshot = null,
    onStartFromWatchlist,
    sizingDefaultsControl = null,
    sizingSettingsCommandRef = null,
}) {
    const {
        data,
        ui,
        snack,
        handlers,
    } = usePositionSizing({
        posSizingData,
        setPosSizingData,
        serverMaxPosSize,
        setServerMaxPosSize,
        serverNormalVol,
        setServerNormalVol,
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        onPosSizingSaved,
        onSavingChange: onSavingStateChange,
        onPendingChangesChange: onPendingChangesStateChange,
        posSizingPublicationBaseRef,
        onStartFromWatchlist,
    });

    // The Sizing defaults dialog applies Max Pos Size and Normal Vol through
    // the same save as the toolbar inputs.
    if (sizingSettingsCommandRef) {
        sizingSettingsCommandRef.current = handlers.commitSizingSettings;
    }

    const tableRef = useRef(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [addWatchlistDialogOpen, setAddWatchlistDialogOpen] = useState(false);
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const editingCellKeysRef = useRef(new Set());
    const [activeGridEditCount, setActiveGridEditCount] = useState(0);
    const scaffoldRows = useMemo(
        () => Array.from({ length: EMPTY_POS_SIZING_ROW_COUNT }, (_, index) => ({
            id: `pos-sizing-empty-${index}`,
            _isPlaceholder: true,
            toDelete: false,
        })),
        [],
    );
    const listingLabelsByCognive = useMemo(() => {
        const labels = new Map();
        (Array.isArray(industries) ? industries : []).forEach((row) => {
            const cogniveId = normalizeCogniveId(row?.cognive_id);
            if (cogniveId && row?.msi_listing_label != null && !labels.has(cogniveId)) {
                labels.set(cogniveId, row.msi_listing_label);
            }
        });
        return labels;
    }, [industries]);
    const watchlistAttributeDefinitions = useMemo(() => (
        (Array.isArray(watchlistAttributeSnapshot?.definitions)
            ? watchlistAttributeSnapshot.definitions
            : []
        ).filter(isWatchlistGroupingAttribute)
    ), [watchlistAttributeSnapshot]);
    const watchlistAttributeValuesByCognive = useMemo(() => {
        const allowedDefinitionIds = new Set(
            watchlistAttributeDefinitions.map(
                (definition) => String(definition.attribute_definition_id),
            ),
        );
        const valuesByCognive = new Map();
        (Array.isArray(watchlistAttributeSnapshot?.values)
            ? watchlistAttributeSnapshot.values
            : []
        ).forEach((item) => {
            const cogniveId = normalizeCogniveId(item?.cognive_id);
            const definitionId = String(item?.attribute_definition_id || '');
            if (!cogniveId || !allowedDefinitionIds.has(definitionId)) return;
            if (!valuesByCognive.has(cogniveId)) {
                valuesByCognive.set(cogniveId, new Map());
            }
            valuesByCognive.get(cogniveId).set(definitionId, item?.value_text ?? '');
        });
        return valuesByCognive;
    }, [watchlistAttributeDefinitions, watchlistAttributeSnapshot]);
    const decoratedRows = useMemo(() => (data.rows || []).map((row) => {
        const uploadedIdentifier = getUploadedIdentifierForRow(row);
        const cogniveId = normalizeCogniveId(row?.cognive_id);
        return {
            ...row,
            display_identifier: uploadedIdentifier.value,
            display_identifier_type: uploadedIdentifier.type,
            msi_listing_label: cogniveId == null
                ? null
                : (
                    row?.msi_listing_label
                    ?? listingLabelsByCognive.get(cogniveId)
                    ?? null
                ),
        };
    }), [data.rows, listingLabelsByCognive]);
    const originalRowsById = useMemo(
        () => new Map((data.originalRows || []).map((row) => [row.id, row])),
        [data.originalRows],
    );
    const rowsForGrid = decoratedRows.length === 0
        ? scaffoldRows
        : decoratedRows;
    const outputHeatmap = useMemo(
        () => buildPositionSizingOutputHeatmap(decoratedRows),
        [decoratedRows],
    );
    const tableBusy = Boolean(loading || ui.saving);
    const isInitialLoading = Boolean(loading && decoratedRows.length === 0);
    // Unsaved new rows stay on top, unsorted, until they are saved.
    const hasNewRows = data.rows.some((row) => row.isNewRow);

    // Counts the cells being edited, for onEditingStateChange.
    const handleCellEditingChange = useCallback((editKey, editing) => {
        if (editingCellKeysRef.current.has(editKey) === editing) {
            return;
        }
        if (editing) editingCellKeysRef.current.add(editKey);
        else editingCellKeysRef.current.delete(editKey);
        setActiveGridEditCount(editingCellKeysRef.current.size);
    }, []);

    useEffect(() => {
        onEditingStateChange?.(activeGridEditCount > 0);
    }, [activeGridEditCount, onEditingStateChange]);

    useEffect(() => () => {
        onEditingStateChange?.(false);
    }, [onEditingStateChange]);

    useEffect(() => {
        onVisualizationRowsChange?.(data.rows);
    }, [data.rows, onVisualizationRowsChange]);

    // A point picked in the scatter plot: bring its row into view and focus
    // its identity cell, on the next frame (scrolling re-renders the rows
    // synchronously, which React does not allow while it runs effects).
    useEffect(() => {
        if (!linkedRowId || linkedRowRequestId === 0) return undefined;
        const animationFrameId = window.requestAnimationFrame(() => {
            tableRef.current?.scrollToRow(linkedRowId, { focusColumnId: 'identity_summary' });
        });
        return () => window.cancelAnimationFrame(animationFrameId);
    }, [linkedRowId, linkedRowRequestId]);

    useEffect(() => () => {
        onVisualizationRowsChange?.([]);
    }, [onVisualizationRowsChange]);

    const unitLabel =
        displayUnit === 'mm'
            ? '($ mm)'
            : displayUnit === 'k'
            ? '($ k)'
            : '($)';
    const warningSections = [
        {
            key: 'ignored_watchlist_cognive_ids',
            label: 'Incomplete Position Sizing data (not added)',
            showIdentifiers: true,
        },
        { key: 'dropped_watchlist_cognive_ids', label: 'Dropped when saving what-if (not in watchlist)' },
        { key: 'invalid_gmv_cognive_ids', label: 'Invalid GMV (skipped)' },
        { key: 'dropped_cognive_ids_from_calc', label: 'Excluded from calculation (missing factor data)' },
        { key: 'failed_trade_idea_infer_cognive_ids', label: 'Trade idea inference failed' },
    ];

    // Saved rows whose input differs from what was loaded or last saved.
    const isDirtyInput = (row, field) => originalRowsById.get(row.id)?.[field] !== row[field];
    // Trade idea and position size: entering one locks the other.
    const isLockedInput = (row, field) => (
        (field === 'trade_idea' && !ui.isTradeEditable({ id: row.id, row }))
        || (field === 'pos_gmv_dollar' && !ui.isGmvEditable({ id: row.id, row }))
    );
    // Edits made during a save are queued, so saving does not lock cells.
    const isInputEditable = (row, field) => (
        !readOnly && !row._isPlaceholder && !row.toDelete && !isLockedInput(row, field)
    );
    const inputCellClassName = (field, row, value) => {
        const negative = isNegative(value) && negativeCell;
        if (row.toDelete) return negative;
        if (row.isNewRow) {
            return cx(negative, isLockedInput(row, field) && INPUT_CELL_CLASS_NAMES.lockedDraft);
        }
        if (ui.saving) return cx(negative, INPUT_CELL_CLASS_NAMES.input);
        if (isLockedInput(row, field)) return cx(negative, INPUT_CELL_CLASS_NAMES.locked);
        return cx(
            negative,
            isDirtyInput(row, field) ? INPUT_CELL_CLASS_NAMES.dirty : INPUT_CELL_CLASS_NAMES.input,
        );
    };
    // A commit hands the hook the row as the grid's row update did: the
    // displayed row with the new value, and the displayed row before it.
    const commitInput = (row, field, nextValue) => {
        data.processRowUpdate({ ...row, [field]: nextValue }, row);
    };

    const identityLabel = securityIdentityFieldLabel(identityDisplayField);
    // Position size in dollars shows whole dollars; thousands and millions
    // keep two decimals, which are already coarser than a dollar.
    const positionSizeFractionDigits = displayUnit === 'mm' || displayUnit === 'k' ? 2 : 0;
    const inputColumn = ({ id, header, align = 'right', sortFn, maximumFractionDigits }) => ({
        id,
        header,
        size: CELL_WIDTH,
        accessorFn: (row) => row[id],
        sortFn,
        cell: ({ row: { original } }) => {
            if (original._isPlaceholder) return null;
            const rowLabel = securityIdentityPresentation(original, 'ticker').value;
            return (
                <EditableCellWithUndo
                    value={original[id]}
                    format={(value) => formatValue(value, {
                        field: id,
                        header,
                        isNewRow: original.isNewRow,
                        maximumFractionDigits,
                    })}
                    formatInput={(value) => formatInputValue(value, { field: id, header, maximumFractionDigits })}
                    align={align}
                    editable={isInputEditable(original, id)}
                    aria-label={`${header} for ${rowLabel}`}
                    onCommit={(nextValue) => commitInput(original, id, nextValue)}
                    onEditingChange={(editing) => handleCellEditingChange(`${original.id}:${id}`, editing)}
                    showUndo={
                        !original.isNewRow && !original.toDelete && !ui.saving && isDirtyInput(original, id)
                    }
                    undoLabel={`Undo ${header} change for ${rowLabel}`}
                    onUndo={() => handlers.handleUndoCell(original.id, id)}
                />
            );
        },
        meta: {
            align,
            cellClassName: ({ row, getValue }) => inputCellClassName(id, row.original, getValue()),
        },
    });
    // A calculated or market value: red when negative, or tinted by the
    // output heat map.
    const valueColumn = ({ id, header, heatmap = false }) => ({
        id,
        header,
        size: CELL_WIDTH,
        accessorFn: (row) => row[id],
        sortFn: sortByNumber,
        cell: ({ row: { original } }) => (
            original._isPlaceholder ? null : formatValue(original[id], { field: id, header })
        ),
        meta: {
            align: 'right',
            cellClassName: ({ row, getValue }) => (
                heatmap
                    ? outputHeatmapClassName(outputHeatmap, id, row.original.id)
                    : isNegative(getValue()) && negativeCell
            ),
        },
    });
    const watchlistAttributeColumns = watchlistAttributeDefinitions.map((definition) => {
        const definitionId = String(definition.attribute_definition_id);
        return {
            id: `watchlist_attribute:${definitionId}`,
            header: definition.display_name,
            size: 155,
            accessorFn: (row) => {
                const cogniveId = normalizeCogniveId(row?.cognive_id);
                return cogniveId == null
                    ? ''
                    : (watchlistAttributeValuesByCognive.get(cogniveId)?.get(definitionId) ?? '');
            },
            sortFn: sortByText,
            meta: { description: 'Watchlist attribute saved with this Position Sizing draft.' },
        };
    });
    // Rebuilt every render: the cells read the hook's per-render state
    // (saving, editability, the loaded rows).
    const columns = [
        {
            id: 'row_actions',
            header: '',
            size: 56,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                if (readOnly || original._isPlaceholder) {
                    return null;
                }
                if (original.isNewRow) {
                    return (
                        <KitTooltip content="Remove new row">
                            <span className="inline-flex">
                                <KitIconButton
                                    label="Remove new row"
                                    disabled={ui.saving}
                                    onClick={() => handlers.handleRemoveNewRow(original.id)}
                                >
                                    <Undo2 size={18} strokeWidth={2} aria-hidden="true" />
                                </KitIconButton>
                            </span>
                        </KitTooltip>
                    );
                }
                return (
                    <KitTooltip content="Delete security">
                        <span className="inline-flex">
                            <KitIconButton
                                label="Delete security"
                                disabled={ui.saving}
                                onClick={() => setDeleteCandidate({
                                    id: original.id,
                                    sedol: original.sedol,
                                    ticker: original.ticker,
                                    display_identifier: original.display_identifier,
                                    display_identifier_type: original.display_identifier_type,
                                })}
                            >
                                <Trash2 size={18} strokeWidth={2} aria-hidden="true" />
                            </KitIconButton>
                        </span>
                    </KitTooltip>
                );
            },
            meta: { align: 'center', label: 'Row actions', wrap: true },
        },
        {
            id: 'identity_summary',
            header: identityLabel,
            size: 250,
            accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
            sortFn: sortByText,
            cell: ({ row: { original } }) => {
                if (original._isPlaceholder) return null;
                const presentation = securityIdentityPresentation(original, identityDisplayField);
                return (
                    <KitTooltip content={presentation.tooltip || presentation.value}>
                        <span
                            aria-label={`${identityLabel}: ${presentation.value}`}
                            className="block min-w-0 truncate"
                        >
                            {presentation.value}
                        </span>
                    </KitTooltip>
                );
            },
            meta: {
                cellClassName: identityDisplayField === 'submitted_identifier' ? 'font-mono' : undefined,
                headerControls: () => (
                    <SecurityIdentityFieldMenu value={identityDisplayField} onChange={setIdentityDisplayField} />
                ),
            },
        },
        inputColumn({
            id: 'trade_idea',
            header: 'Trade idea',
            align: 'right',
            sortFn: sortByAbsoluteNumber,
        }),
        inputColumn({
            id: 'pos_gmv_dollar',
            header: `Pos Size / Exp ${unitLabel}`,
            align: 'right',
            sortFn: sortByAbsoluteNumber,
            maximumFractionDigits: positionSizeFractionDigits,
        }),
        inputColumn({ id: 'custom_liquidity', header: 'Liq. Factor (Custom) (%)', sortFn: sortByNumber }),
        inputColumn({ id: 'custom_idio_vol', header: 'Custom Idio Vol (%)', sortFn: sortByNumber }),
        ...watchlistAttributeColumns,
        valueColumn({ id: 'price', header: 'Price ($)' }),
        valueColumn({ id: 'dollar_adtv', header: 'ADTV ($)' }),
        valueColumn({ id: 'idio_vol_dollar', header: `Idio Vol ${unitLabel}`, heatmap: true }),
        valueColumn({ id: 'idio_var_pct', header: 'Idio Var (%)', heatmap: true }),
        valueColumn({ id: 'liquidity_factor_pct', header: 'Liq. Factor (ADTV) (%)', heatmap: true }),
        valueColumn({ id: 'pos_gmv_base_pct', header: 'Pos Size (1) (%)', heatmap: true }),
        valueColumn({ id: 'pos_size_idio_adj_pct', header: 'Pos Size (Idio Adj) (%)', heatmap: true }),
        valueColumn({ id: 'adtv_pct', header: 'ADTV (%)', heatmap: true }),
        valueColumn({ id: 'edge_dollar', header: `Edge ${unitLabel}`, heatmap: true }),
    ];

    // A row's state, as `data-row-state` and as its background (which the
    // pinned cells follow); the scatter-linked row keeps the selected tint.
    const rowState = (row) => {
        if (row._isPlaceholder) return 'placeholder';
        if (row.toDelete) return 'deleted';
        if (ui.isIncompleteNewDraftRow(row.id)) return 'incomplete-draft';
        if (ui.isCompletedNewDraftRow(row.id)) return 'complete-draft';
        return null;
    };
    const linkedRowKey = linkedRowId == null ? null : String(linkedRowId);
    const getRowClassName = (row) => {
        const state = rowState(row);
        if (String(row.id) !== linkedRowKey) return ROW_STATE_CLASS_NAMES[state];
        return state === 'deleted' ? ROW_STATE_CLASS_NAMES.deletedLinked : undefined;
    };
    const getRowProps = (row) => {
        const state = rowState(row);
        return state ? { 'data-row-state': state } : undefined;
    };

    const handleDownload = () => {
        const unitLabel =
            displayUnit === 'mm'
                ? '($ mm)'
                : displayUnit === 'k'
                ? '($ k)'
                : '($)';
        const exportData = data?.rows.map((row) => {
            const uploadedIdentifier = getUploadedIdentifierForRow(row);
            const cogniveId = normalizeCogniveId(row?.cognive_id);
            const attributeValues = cogniveId == null
                ? null
                : watchlistAttributeValuesByCognive.get(cogniveId);
            const exportedAttributes = Object.fromEntries(
                watchlistAttributeDefinitions.map((definition) => [
                    `Attribute: ${definition.display_name}`,
                    attributeValues?.get(String(definition.attribute_definition_id)) ?? '',
                ]),
            );
            return ({
            "Identifier": uploadedIdentifier.value
                ? [uploadedIdentifier.type, uploadedIdentifier.value].filter(Boolean).join(' ')
                : '',
            "Ticker": row.ticker,
            "Trade idea": row.trade_idea !== undefined ? row.trade_idea : "",
            [`Pos Size / Exp ${unitLabel}`]: row.pos_gmv_dollar !== undefined ? row.pos_gmv_dollar : "",
            "Liq. Factor (Custom) (%)": row.custom_liquidity !== undefined ? row.custom_liquidity : "",
            "Custom Idio Vol (%)": row.custom_idio_vol !== undefined ? row.custom_idio_vol : "",
            ...exportedAttributes,
            "Price ($)": row.price !== undefined ? row.price : "",
            "ADTV ($)": row.dollar_adtv !== undefined ? row.dollar_adtv : "",
            [`Idio Vol ${unitLabel}`]: row.idio_vol_dollar !== undefined ? row.idio_vol_dollar : "",
            "Idio Var (%)": row.idio_var_pct !== undefined ? row.idio_var_pct : "",
            "Liq. Factor (ADTV) (%)": row.liquidity_factor_pct !== undefined ? row.liquidity_factor_pct : "",
            "Pos Size (1) (%)": row.pos_gmv_base_pct !== undefined ? row.pos_gmv_base_pct : "",
            "Pos Size (Idio Adj) (%)": row.pos_size_idio_adj_pct !== undefined ? row.pos_size_idio_adj_pct : "",
            "ADTV (%)": row.adtv_pct !== undefined ? row.adtv_pct : "",
            [`Edge ${unitLabel}`]: row.edge_dollar !== undefined ? row.edge_dollar : "",
            });
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'posiztion_sizing');
        XLSX.writeFile(wb, 'Possizing_position_sizing.xlsx');
    };

    const content = (
        <BusyOverlay
            show={tableBusy && !isInitialLoading}
            minHeight={WORKSPACE_DIMENSIONS.primaryTableHeight + 40}
            spinnerSize={26}
        >
            <Paper
                className={cardClasses}
                elevation={1}
                sx={{ minWidth: 0, ...analyticsPanelSx, ...compactPanelPaddingSx }}
            >
                <DashboardToolbarPortal active={toolbarActive}>
                    <Box sx={{ ...workspaceActionClusterSx, ml: 0, flexWrap: 'nowrap', gap: 0.75 }}>
                        <ToolbarInputs
                            maxPosSizeStr={ui.localMaxPosSizeStr}
                            normalVolStr={ui.localNormalVolStr}
                            setMaxPosSizeStr={ui.setLocalMaxPosSizeStr}
                            setNormalVolStr={ui.setLocalNormalVolStr}
                            setLocalMaxPosSize={ui.setLocalMaxPosSize}
                            setLocalNormalVol={ui.setLocalNormalVol}
                            serverMaxPosSize={serverMaxPosSize}
                            serverNormalVol={serverNormalVol}
                            maxPosSizeIsDirty={ui.maxPosSizeIsDirty}
                            normalVolIsDirty={ui.normalVolIsDirty}
                            onUndoMaxPosSize={ui.handleMaxPosSizeClear}
                            onUndoNormalVol={ui.handleNormalVolClear}
                            onMaxPosSizeCommit={handlers.handleMaxPosSizeCommit}
                            onNormalVolCommit={handlers.handleNormalVolCommit}
                            disabled={ui.saving || readOnly}
                        />
                        {ui.outstandingNewDraftCount > 0 ? (
                            <Chip
                                role="status"
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={`${ui.outstandingNewDraftCount} new securit${
                                    ui.outstandingNewDraftCount === 1 ? 'y needs' : 'ies need'
                                } sizing inputs.`}
                                sx={{
                                    height: 25,
                                    maxWidth: 235,
                                    '& .MuiChip-label': {
                                        px: 0.9,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                    },
                                }}
                            />
                        ) : null}
                        {contextUpdatesControl}
                        {importsControl}
                        {sizingDefaultsControl}
                    </Box>
                </DashboardToolbarPortal>

                <CompactPanelHeader title="Position Details">
                    <Tooltip title="Add watchlist securities">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setAddWatchlistDialogOpen(true)}
                                disabled={ui.saving || readOnly}
                                aria-label="Add watchlist securities"
                                sx={compactIconButtonSx}
                            >
                                <AddIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download position sizing">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleDownload}
                                aria-label="Download position sizing"
                                disabled={ui.saving || data.rows.length === 0}
                                sx={compactIconButtonSx}
                            >
                                <FileDownloadOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </CompactPanelHeader>

                <DataTable
                    ref={tableRef}
                    aria-label="Position sizing"
                    data={rowsForGrid}
                    columns={columns}
                    sorting={hasNewRows ? NO_SORTING : ui.sorting}
                    onSortingChange={handlers.handleSortingChange}
                    pinnedColumns={PINNED_COLUMNS}
                    getRowClassName={getRowClassName}
                    getRowProps={getRowProps}
                    selectedRowId={linkedRowId}
                    loading={isInitialLoading}
                    height={WORKSPACE_DIMENSIONS.primaryTableHeight}
                    virtualize
                />
            </Paper>
        </BusyOverlay>
    );

    return (
        <>
            {wrapInGridItem ? (
                <Grid2 size={gridSize}>
                    {content}
                </Grid2>
            ) : content}

            <Dialog
                open={ui.warningDialogOpen}
                onClose={() => ui.setWarningDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save Warnings</DialogTitle>
                <DialogContent dividers>
                    {ui.warningSummary ? (
                        warningSections.map(section => {
                            const items = ui.warningSummary[section.key] || [];
                            if (!items.length) return null;
                            return (
                                <Box key={section.key} sx={{ mb: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {section.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {section.showIdentifiers
                                            ? `${items.slice(0, 10).join(', ')}${items.length > 10 ? `, and ${items.length - 10} more` : ''}`
                                            : `${items.length} securit${items.length === 1 ? 'y' : 'ies'}`}
                                    </Typography>
                                </Box>
                            );
                        })
                    ) : (
                        <Typography variant="body2">No warnings.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => ui.setWarningDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(deleteCandidate)}
                onClose={() => setDeleteCandidate(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Security</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        {(() => {
                            const identifierValue = deleteCandidate?.display_identifier || '';
                            const identifierType = identifierValue
                                ? (deleteCandidate?.display_identifier_type || '')
                                : '';
                            const identifierLabel = identifierValue
                                ? [identifierType, identifierValue].filter(Boolean).join(' ')
                                : '';
                            const tickerSuffix = deleteCandidate?.ticker
                                ? `${identifierLabel ? ' ' : ''}${deleteCandidate.ticker}`
                                : '';
                            return `Delete ${identifierLabel}${tickerSuffix}?`;
                        })()}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCandidate(null)} disabled={ui.saving}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={ui.saving}
                        onClick={() => {
                            if (deleteCandidate?.id != null) {
                                handlers.handleToggleDelete(deleteCandidate.id);
                            }
                            setDeleteCandidate(null);
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <AddWatchlistSecuritiesDialog
                open={addWatchlistDialogOpen}
                watchlistRows={industries}
                existingRows={data.rows}
                onClose={() => setAddWatchlistDialogOpen(false)}
                onConfirm={handlers.handleAddWatchlistRows}
            />

            <Snackbar open={!!ui.error} autoHideDuration={4000} onClose={() => ui.setError('')}>
                <Alert severity="error">{ui.error}</Alert>
            </Snackbar>

            <Snackbar open={snack.deletedCount > 0} autoHideDuration={3000} onClose={() => snack.setDeletedCount(0)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="info">{snack.deletedCount} row{snack.deletedCount > 1 ? 's' : ''} deleted</Alert>
            </Snackbar>

            <Snackbar open={snack.addedCount > 0} autoHideDuration={3000} onClose={() => snack.setAddedCount(0)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="success">{snack.addedCount} new row{snack.addedCount > 1 ? 's' : ''} added</Alert>
            </Snackbar>

            <Snackbar open={snack.dropSnackbarOpen} autoHideDuration={5000} onClose={() => snack.setDropSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="warning">
                    Pos-sizing calculations could not be completed for {ui.droppedCogniveIds.length} securit{ui.droppedCogniveIds.length === 1 ? 'y' : 'ies'} with missing factor data.
                </Alert>
            </Snackbar>

            <Snackbar open={snack.invalidGmvSnackbarOpen} autoHideDuration={5000} onClose={() => snack.setInvalidGmvSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="warning">
                    Skipped {ui.invalidGmvCogniveIds.length} securit{ui.invalidGmvCogniveIds.length === 1 ? 'y' : 'ies'} due to invalid GMV values.
                </Alert>
            </Snackbar>

            <Snackbar open={snack.failedTradeIdeaInferSnackOpen} autoHideDuration={5000} onClose={() => snack.setFailedTradeIdeaInferSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="warning">
                    Trade ideas could not be inferred for {snack.failedTradeIdeaInferCogniveIds.length} securit{snack.failedTradeIdeaInferCogniveIds.length === 1 ? 'y' : 'ies'}. Falling back to existing values.
                </Alert>
            </Snackbar>

        </>
    );
}

PositionSizingTable.propTypes = {
    posSizingData: PropTypes.array.isRequired,
    industries: PropTypes.array,
    setPosSizingData: PropTypes.func.isRequired,
    displayUnit: PropTypes.string.isRequired,
    serverMaxPosSize: PropTypes.number,
    serverNormalVol: PropTypes.number,
    // The member's starting values; absent until they load.
    defaultTargetVolPct: PropTypes.number,
    defaultTargetVolDollar: PropTypes.number,
    defaultScaler: PropTypes.number,
    onPosSizingSaved: PropTypes.func,
    onEditingStateChange: PropTypes.func,
    onSavingStateChange: PropTypes.func,
    onPendingChangesStateChange: PropTypes.func,
    linkedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linkedRowRequestId: PropTypes.number,
    onVisualizationRowsChange: PropTypes.func,
    importsControl: PropTypes.node,
    contextUpdatesControl: PropTypes.node,
    toolbarActive: PropTypes.bool,
    wrapInGridItem: PropTypes.bool,
    gridSize: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
    loading: PropTypes.bool,
    readOnly: PropTypes.bool,
    posSizingPublicationBaseRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    watchlistAttributeSnapshot: PropTypes.shape({
        definitions: PropTypes.arrayOf(PropTypes.object).isRequired,
        values: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
    onStartFromWatchlist: PropTypes.func,
    sizingDefaultsControl: PropTypes.node,
    sizingSettingsCommandRef: PropTypes.shape({ current: PropTypes.func }),
};
